import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingAccounts } from '@/hooks/useTradingAccounts';
import { useTradingProfileDirectory } from '@/hooks/useTradingProfileDirectory';

// Import the types we need
import type { CategoryPositionRule, DirectionMaxRule, PositionSizeRule } from '@einhaus/project314-types';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { showToast } from '@/components/Layout/Toast';
import ResetPortfolioModal from './components/ResetPortfolioModal';
import DirectionalRuleModal from './components/DirectionalRuleModal';
import CategoryRuleModal from './components/CategoryRuleModal';
import PositionSizeRuleModal from './components/PositionSizeRuleModal';
import AddStrategyModal from './components/AddStrategyModal';
import TradingProfileModal from './components/TradingProfileModal';
import TradingAccountCard from './components/TradingAccountCard';
import PositionApprovalCard from './components/PositionApprovalCard';
import NotificationsCard from './components/NotificationsCard';
import DirectionalRulesCard from './components/DirectionalRulesCard';
import CategoryRulesCard from './components/CategoryRulesCard';
import PositionSizeRulesCard from './components/PositionSizeRulesCard';
import StrategiesSubscribedCard from './components/StrategiesSubscribedCard';
import PortfolioGovernanceCard from './components/PortfolioGovernanceCard';
import PortfolioAdmissionCard from './components/PortfolioAdmissionCard';
import PortfolioGovernanceDecisionsCard from './components/PortfolioGovernanceDecisionsCard';
import PortfolioSubscriptionChangesCard from './components/PortfolioSubscriptionChangesCard';
import { CONFIGURE_STRATEGY_PARAM } from './components/strategySubscription';

const PortfolioSettings: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    // Local state for rules (will be populated from portfolio data)
    const [directionalLimitRules, setDirectionalLimitRules] = useState<DirectionMaxRule[]>([]);
    const [categoryLimitRules, setCategoryLimitRules] = useState<CategoryPositionRule[]>([]);
    const [positionSizeRules, setPositionSizeRules] = useState<PositionSizeRule[]>([]);

    // Modal states
    const [showDirectionalRuleModal, setShowDirectionalRuleModal] = useState(false);
    const [showCategoryRuleModal, setShowCategoryRuleModal] = useState(false);
    const [showPositionSizeRuleModal, setShowPositionSizeRuleModal] = useState(false);
    const [showStrategyModal, setShowStrategyModal] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showTradingProfileModal, setShowTradingProfileModal] = useState(false);
    const [tradingProfileStrategyId, setTradingProfileStrategyId] = useState<number | null>(null);

    // Editing state
    const [editingDirectionalRuleId, setEditingDirectionalRuleId] = useState<string | null>(null);
    const [editingCategoryRuleId, setEditingCategoryRuleId] = useState<string | null>(null);
    const [editingPositionSizeRuleId, setEditingPositionSizeRuleId] = useState<string | null>(null);

    // New rule form states
    const [newDirectionalRule, setNewDirectionalRule] = useState<Partial<DirectionMaxRule>>({
        direction: 'either',
        type: 'positions',
        value: 0
    });

    const [newCategoryRule, setNewCategoryRule] = useState<Partial<CategoryPositionRule>>({
        categoryLimitDirection: 'either',
        categoryLimitType: 'exactCategoriesMatch',
        categoryLimitMaximumType: 'positions',
        value: 0
    });

    const [newPositionSizeRule, setNewPositionSizeRule] = useState<Partial<PositionSizeRule>>({
        type: 'fixed',
        entityTypes: ['Asset'],
        value: 0
    });

    // Fetch portfolio data with tRPC
    const portfolioQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolio.queryOptions({
                id: id ? parseInt(id) : 0,
                shouldFetchTrades: true
            }),
            enabled: !!id && parseInt(id) > 0
        },
        {
            fallbackErrorMessage: 'Failed to load portfolio',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    );

    // Fetch trading accounts via the shared hook
    const { tradingAccounts, query: tradingAccountsQuery } = useTradingAccounts({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading accounts',
            showErrorToast: true,
            setError: (msg) => setError(msg)
        }
    });

    // Owned and shared trading profiles, for subscription names and the profile picker
    const {
        directory: tradingProfileDirectory,
        isLoaded: areTradingProfilesLoaded,
        tradingProfileOptions
    } = useTradingProfileDirectory({ setError });

    // Extract data from portfolio query
    const portfolio = portfolioQuery.data?.portfolios?.[0] || null;

    const portfolioAsAddTarget = useMemo(() => (portfolio ? [portfolio] : []), [portfolio]);

    const isLoading = !areTradingProfilesLoaded || [portfolioQuery, tradingAccountsQuery].some((q) => q.status !== 'success');

    // Process portfolio settings when data is loaded
    useEffect(() => {
        if (!portfolio?.settings) return;

        const { directionMaxRules, categoryPositionRules, positionSizeRules } = portfolio.settings;

        // Update state with the extracted data
        setDirectionalLimitRules(
            directionMaxRules?.map((rule: DirectionMaxRule) => ({
                id: rule.id || `dir-${Math.random().toString(36).substring(2, 11)}`,
                direction: rule.direction,
                type: rule.type,
                value: rule.value
            })) || []
        );

        setCategoryLimitRules(categoryPositionRules || []);

        setPositionSizeRules(
            positionSizeRules?.map((rule: PositionSizeRule) => ({
                id: rule.id || `pos-${Math.random().toString(36).substring(2, 11)}`,
                type: rule.type,
                entityTypes: rule.entityTypes || ['Asset'],
                value: rule.value
            })) || []
        );
    }, [portfolio?.settings]);

    // State for optimistic UI updates
    const [optimisticSettings, setOptimisticSettings] = useState({
        requireBuyApproval: false,
        buyNotifications: false,
        sellNotifications: false
    });

    // Track pending mutations to prevent double-clicks
    const [pendingMutations, setPendingMutations] = useState<Record<string, boolean>>({});

    // Re-seed optimistic settings from server data when the portfolio's settings change. Adjusting state
    // during render (guarded by the synced reference) is React's recommended alternative to a sync effect,
    // and preserves the card's in-between optimistic edits.
    const [syncedSettings, setSyncedSettings] = useState<object | null>(null);

    if (portfolio?.settings && portfolio.settings !== syncedSettings) {
        setSyncedSettings(portfolio.settings);

        setOptimisticSettings({
            requireBuyApproval: Boolean(portfolio.settings.requireBuyApproval),
            buyNotifications: Boolean(portfolio.settings.buyNotifications),
            sellNotifications: Boolean(portfolio.settings.sellNotifications)
        });
    }

    const closeDirectionalRuleModal = useCallback(() => {
        setShowDirectionalRuleModal(false);
        setEditingDirectionalRuleId(null);

        setNewDirectionalRule({
            direction: 'either',
            type: 'positions',
            value: 0
        });
    }, []);

    const closeCategoryRuleModal = useCallback(() => {
        setShowCategoryRuleModal(false);
        setEditingCategoryRuleId(null);

        setNewCategoryRule({
            categoryLimitDirection: 'either',
            categoryLimitType: 'exactCategoriesMatch',
            categoryLimitMaximumType: 'positions',
            value: 0
        });
    }, []);

    const closePositionSizeRuleModal = useCallback(() => {
        setShowPositionSizeRuleModal(false);
        setEditingPositionSizeRuleId(null);

        setNewPositionSizeRule({
            type: 'fixed',
            entityTypes: ['Asset'],
            value: 0
        });
    }, []);

    const closeTradingProfileModal = useCallback(() => {
        setShowTradingProfileModal(false);
        setTradingProfileStrategyId(null);
    }, []);

    const openTradingProfileModal = useCallback((strategyId: number) => {
        setTradingProfileStrategyId(strategyId);
        setShowTradingProfileModal(true);
    }, []);

    // Other pages link here via `buildConfigureStrategySubscriptionPath` to open one subscription's deployment editor directly.
    const [searchParams, setSearchParams] = useSearchParams();
    const configureStrategyParam = searchParams.get(CONFIGURE_STRATEGY_PARAM);

    useEffect(() => {
        if (configureStrategyParam === null || isLoading || !portfolio) return;

        const strategyId = Number(configureStrategyParam);

        if (portfolio.strategiesSubscribed?.some((strategy) => strategy.id === strategyId)) {
            openTradingProfileModal(strategyId);
        } else {
            showToast(`Strategy #${configureStrategyParam} is not subscribed to this portfolio`, 'error');
        }

        setSearchParams(
            (currentParams) => {
                const nextParams = new URLSearchParams(currentParams);
                nextParams.delete(CONFIGURE_STRATEGY_PARAM);

                return nextParams;
            },
            { replace: true }
        );
    }, [configureStrategyParam, isLoading, openTradingProfileModal, portfolio, setSearchParams]);

    // Mutation for deleting rules
    const deleteRuleMutation = useTrpcMutation(
        trpc.user.removePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(id || '0'), shouldFetchTrades: true }).queryKey
                });
            }
        }),
        {
            successMessage: 'Rule deleted successfully',
            fallbackErrorMessage: 'Failed to delete rule'
        }
    );

    const handleDeleteRule = useCallback(
        (ruleId: string, type: 'directionMax' | 'categoryLimit' | 'positionSizeRule') => {
            if (!portfolio?.id) return;

            void deleteRuleMutation.mutateAsync({
                portfolioId: portfolio.id,
                settingType: type,
                id: ruleId
            });

            // Optimistically update UI
            if (type === 'directionMax') {
                setDirectionalLimitRules((currentRules) => currentRules.filter((rule) => rule.id !== ruleId));
            } else if (type === 'categoryLimit') {
                setCategoryLimitRules((currentRules) => currentRules.filter((rule) => rule.id !== ruleId));
            } else if (type === 'positionSizeRule') {
                setPositionSizeRules((currentRules) => currentRules.filter((rule) => rule.id !== ruleId));
            }
        },
        [portfolio, deleteRuleMutation, setDirectionalLimitRules, setCategoryLimitRules, setPositionSizeRules]
    );

    // Periodically refresh settings groups
    useEffect(() => {
        const interval = setInterval(() => {
            void queryClient.invalidateQueries({
                queryKey: trpc.user.fetchTradingProfiles.queryOptions().queryKey
            });

            void queryClient.invalidateQueries({
                queryKey: trpc.resourceAccess.listAccessibleResources.queryOptions({ resourceType: 'tradingProfile' }).queryKey
            });
        }, 10000); // Refresh every 10 seconds

        return () => clearInterval(interval);
    }, [queryClient]);

    return (
        <UserLayout
            title={
                isLoading
                    ? portfolio?.name
                        ? `${portfolio.name} - Settings - Project 314`
                        : 'Portfolio Settings - Project 314'
                    : error
                      ? portfolio?.name
                          ? `${portfolio.name} - Settings - Error - Project 314`
                          : 'Portfolio Settings - Error - Project 314'
                      : portfolio?.name
                        ? `${portfolio.name} - Settings - Project 314`
                        : 'Portfolio Settings - Project 314'
            }
        >
            {isLoading ? (
                <div className="flex justify-center items-center my-5">
                    {' '}
                    <DataLoadingSpinner text="Loading portfolio settings..." />
                </div>
            ) : error ? (
                <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
            ) : (
                <div className="container mx-auto mt-3 max-w-4xl">
                    <div className="flex justify-between">
                        <div>
                            <h3 className="text-white mb-3 text-xl font-semibold">{portfolio ? portfolio.name : 'Portfolio'} - Settings</h3>
                        </div>
                    </div>

                    {/* Trading Account Section */}
                    {tradingAccounts.length > 0 && (
                        <TradingAccountCard
                            portfolioId={id}
                            tradingAccountId={portfolio?.tradingAccountId}
                            tradingAccounts={tradingAccounts}
                        />
                    )}

                    {/* Position Approval Rules Section */}
                    <PositionApprovalCard
                        optimisticSettings={optimisticSettings}
                        pendingMutations={pendingMutations}
                        portfolioId={id}
                        setOptimisticSettings={setOptimisticSettings}
                        setPendingMutations={setPendingMutations}
                    />

                    {/* Directional Position Limit Rules Section */}
                    <DirectionalRulesCard
                        rules={directionalLimitRules}
                        setEditingRuleId={setEditingDirectionalRuleId}
                        setNewRule={setNewDirectionalRule}
                        setShowModal={setShowDirectionalRuleModal}
                        onDeleteRule={handleDeleteRule}
                    />

                    {/* Category Limit Rules Section */}
                    <CategoryRulesCard
                        rules={categoryLimitRules}
                        setEditingRuleId={setEditingCategoryRuleId}
                        setNewRule={setNewCategoryRule}
                        setShowModal={setShowCategoryRuleModal}
                        onDeleteRule={handleDeleteRule}
                    />

                    {/* Position Size Rules Section */}
                    <PositionSizeRulesCard
                        rules={positionSizeRules}
                        setEditingRuleId={setEditingPositionSizeRuleId}
                        setNewRule={setNewPositionSizeRule}
                        setShowModal={setShowPositionSizeRuleModal}
                        onDeleteRule={handleDeleteRule}
                    />

                    {/* Strategies Subscribed Section */}
                    <StrategiesSubscribedCard
                        portfolio={portfolio}
                        portfolioPositionSizeRules={positionSizeRules}
                        setShowStrategyModal={setShowStrategyModal}
                        tradingProfileDirectory={tradingProfileDirectory}
                        onConfigureStrategy={openTradingProfileModal}
                    />

                    {/* Position Governance Section (multi-strategy admission control) */}
                    <PortfolioAdmissionCard portfolioId={id} />
                    <PortfolioGovernanceCard portfolioId={id} strategiesSubscribed={portfolio?.strategiesSubscribed} />
                    <PortfolioGovernanceDecisionsCard portfolioId={id} />
                    <PortfolioSubscriptionChangesCard portfolioId={id} strategiesSubscribed={portfolio?.strategiesSubscribed} />

                    {/* Notifications Section */}
                    <NotificationsCard
                        optimisticSettings={optimisticSettings}
                        pendingMutations={pendingMutations}
                        portfolioId={id}
                        setOptimisticSettings={setOptimisticSettings}
                        setPendingMutations={setPendingMutations}
                        showResetButton={!!portfolio}
                        onResetClick={() => setShowResetModal(true)}
                    />
                </div>
            )}

            {/* Reset Portfolio Modal */}
            <ResetPortfolioModal isOpen={showResetModal} portfolioId={id} onClose={() => setShowResetModal(false)} />

            {/* Add Directional Rule Modal */}
            <DirectionalRuleModal
                editingRuleId={editingDirectionalRuleId}
                isOpen={showDirectionalRuleModal}
                newRule={newDirectionalRule}
                portfolioId={id}
                rules={directionalLimitRules}
                setNewRule={setNewDirectionalRule}
                setRules={setDirectionalLimitRules}
                onClose={closeDirectionalRuleModal}
            />

            {/* Add Category Rule Modal */}
            <CategoryRuleModal
                editingRuleId={editingCategoryRuleId}
                isOpen={showCategoryRuleModal}
                newRule={newCategoryRule}
                portfolioId={id}
                rules={categoryLimitRules}
                setNewRule={setNewCategoryRule}
                setRules={setCategoryLimitRules}
                onClose={closeCategoryRuleModal}
            />

            {/* Add Position Size Rule Modal */}
            <PositionSizeRuleModal
                editingRuleId={editingPositionSizeRuleId}
                isOpen={showPositionSizeRuleModal}
                newRule={newPositionSizeRule}
                portfolioId={id}
                rules={positionSizeRules}
                setNewRule={setNewPositionSizeRule}
                setRules={setPositionSizeRules}
                onClose={closePositionSizeRuleModal}
            />

            {/* Add Strategy Modal */}
            {showStrategyModal && portfolio && (
                <AddStrategyModal
                    initialPortfolioId={portfolio.id}
                    portfolios={portfolioAsAddTarget}
                    onClose={() => setShowStrategyModal(false)}
                />
            )}

            {/* Choose Trading Profile Modal */}
            <TradingProfileModal
                isOpen={showTradingProfileModal}
                portfolioId={id}
                portfolioPositionSizeRules={positionSizeRules}
                strategy={portfolio?.strategiesSubscribed?.find((strategy) => strategy.id === tradingProfileStrategyId) ?? null}
                tradingProfileOptions={tradingProfileOptions}
                onClose={closeTradingProfileModal}
            />
        </UserLayout>
    );
};

export default PortfolioSettings;
